import { lazy, Suspense, useState } from 'react'
import { Link } from 'react-router'

import { CategoryGlyph } from '@/components/icons/CategoryGlyph'
import { Icon } from '@/components/icons/Icon'
import { useCollections } from '@/features/collections/api'
import { useTownStore } from '@/features/town/store'
import { categoryGradient } from '@/lib/category'
import { paths } from '@/lib/routes'

import { useCategories } from './api'
import s from './BrowseStrip.module.css'

// Only needed after a tap: keep it out of the first load.
const AllSheet = lazy(() => import('./AllSheet').then((m) => ({ default: m.AllSheet })))

/**
 * One "shop by" row (D-94): the Home collections first, then the main categories, and "All" for
 * every collection, category and sub-category. `strip` = round icons that scroll sideways (phone
 * Home); `bar` = a slim row in the header on laptops, with "All" pinned first like a menu.
 */
export function BrowseStrip({ variant }: { variant: 'strip' | 'bar' }) {
  const town = useTownStore((st) => st.town)
  const { data: categories, isPending } = useCategories()
  const { data: collections = [] } = useCollections(town, { home: true })
  const [open, setOpen] = useState(false)

  const all = (
    <li className={s.allItem}>
      <button
        type="button"
        className={s.item}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className={`${s.ic} ${s.allIc}`} aria-hidden="true">
          <Icon name={variant === 'bar' ? 'menu' : 'more'} size={variant === 'bar' ? 16 : 22} />
        </span>
        <span className={s.label}>All</span>
      </button>
    </li>
  )

  return (
    <nav
      className={variant === 'bar' ? s.bar : s.strip}
      aria-label={variant === 'bar' ? 'Shop by' : 'Collections and categories'}
    >
      <ul className={s.row}>
        {variant === 'bar' && all}
        {collections.map((c) => (
          <li key={`c-${c.slug}`}>
            <Link to={paths.collection(c.slug)} className={s.item}>
              <span
                className={`${s.ic} ${s.collectionIc}`}
                style={{ background: categoryGradient(c) }}
                aria-hidden="true"
              >
                {c.emoji || <Icon name="tag" size={18} />}
              </span>
              <span className={s.label}>{c.title}</span>
            </Link>
          </li>
        ))}
        {collections.length > 0 && <li className={s.divider} aria-hidden="true" />}
        {isPending &&
          Array.from({ length: 8 }, (_, i) => (
            <li key={`sk-${i}`} aria-hidden="true">
              <span className={s.skeleton} />
            </li>
          ))}
        {categories?.map((c) => (
          <li key={c.id}>
            <Link to={paths.category(c.slug)} className={s.item}>
              <span className={s.ic} style={{ background: categoryGradient(c) }} aria-hidden="true">
                <CategoryGlyph icon={c.icon} slug={c.slug} size={variant === 'bar' ? 14 : 26} />
              </span>
              <span className={s.label}>{c.name}</span>
            </Link>
          </li>
        ))}
        {variant === 'strip' && all}
      </ul>
      {open && (
        <Suspense fallback={null}>
          <AllSheet onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </nav>
  )
}
